import {
  Control,
  Controller,
  FieldErrors,
  get,
  useFieldArray,
  UseFormGetValues,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { useEffect, useState } from 'react';
import { PolicyForm } from '../policy-dialog';
import PolicyOrganizationEditDialog from './policy-organization-edit-dialog';

import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AddCircle from '@mui/icons-material/AddCircle';
import ArrowDownward from '@mui/icons-material/ArrowDownward';
import ArrowUpward from '@mui/icons-material/ArrowUpward';
import Delete from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';
import ExpandMore from '@mui/icons-material/ExpandMore';

interface PolicyOrganizationsAccordionProps {
  control: Control<PolicyForm>;
  errors: FieldErrors<PolicyForm>;
  setValue: UseFormSetValue<PolicyForm>;
  watch: UseFormWatch<PolicyForm>;
  getValues: UseFormGetValues<PolicyForm>;
  disabled: boolean;
  fieldName: `actionData.${number}.userModelTemplate.organizations`;
}

export default function PolicyOrganizationsAccordion({
  control,
  errors,
  disabled,
  watch,
  setValue,
  getValues,
  fieldName,
}: PolicyOrganizationsAccordionProps) {
  const [open, setOpen] = useState(false);
  const [openOrganizationDialog, setOpenOrganizationDialog] = useState(false);
  const [clickedIndex, setClickedIndex] = useState(0);

  const { fields, append, remove, move } = useFieldArray({
    control,
    name: fieldName,
  });

  const menuItems = [
    { value: 'custom', label: 'Custom' },
    { value: 'domain_only', label: 'Domain Only' },
    { value: 'school', label: 'School' },
    { value: 'unknown', label: 'Unknown' },
    { value: 'work', label: 'Work' },
  ];

  const orgErrors = get(errors, fieldName);

  // close accordion if there are no items in the accordion
  useEffect(() => {
    if (fields.length === 0) {
      setOpen(false);
    } else {
      setOpen(true);
    }
  }, [fields]);

  // open accordion if there is an error in the accordion
  useEffect(() => {
    if (orgErrors) {
      setOpen(true);
    }
  }, [orgErrors]);

  return (
    <>
      <Accordion
        expanded={open}
        onChange={() => {
          if (fields.length === 0) return;
          setOpen(!open);
        }}
      >
        <AccordionSummary
          expandIcon={
            <IconButton
              disabled={fields.length === 0}
              sx={{ mr: -1 }}
              disableRipple
            >
              <ExpandMore />
            </IconButton>
          }
        >
          <Stack flexDirection="row" alignItems="center" gap={1}>
            <Typography
              sx={{
                color: (theme) =>
                  orgErrors ? theme.palette.error.main : 'inherit',
              }}
            >
              Organizations
            </Typography>
            <Tooltip title="Add" arrow>
              <span>
                <IconButton
                  size="small"
                  disabled={disabled}
                  onClick={(e) => {
                    e.stopPropagation();
                    append({
                      name: '',
                      title: '',
                      primary: fields.find((item) => item.primary)
                        ? false
                        : true,
                      type: 'work',
                      customType: '',
                      department: '',
                      symbol: '',
                      location: '',
                      description: '',
                      domain: '',
                      costCenter: '',
                    });
                  }}
                >
                  <AddCircle />
                </IconButton>
              </span>
            </Tooltip>
            {fields.length > 0 ? (
              <Chip label={fields.length} color="primary" size="small" />
            ) : null}
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 2 }}>
          {fields.map((item, index) => {
            const typeField = watch(`${fieldName}.${index}.type`);

            return (
              <Box key={item.id}>
                <Stack
                  gap={1}
                  flexDirection="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ my: 1.5 }}
                >
                  <Stack flexDirection="row" alignItems="center" gap={2}>
                    <Controller
                      rules={{ required: 'This field is required.' }}
                      name={`${fieldName}.${index}.type`}
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          required
                          inputRef={field.ref}
                          inputProps={{
                            id: `organizations-type-${index}`,
                          }}
                          InputLabelProps={{
                            htmlFor: `organizations-type-${index}`,
                          }}
                          name="organizations"
                          select
                          disabled={disabled}
                          label="Type"
                          error={!!orgErrors?.[index]?.type}
                          helperText={
                            orgErrors?.[index]?.type
                              ? 'This field is required.'
                              : ''
                          }
                          sx={{
                            minWidth: 150,
                          }}
                        >
                          {menuItems.map((item) => (
                            <MenuItem value={item.value} key={item.label}>
                              {item.label}
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                    />
                    {typeField === 'custom' && (
                      <Controller
                        rules={{ required: 'This field is required.' }}
                        name={`${fieldName}.${index}.customType`}
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            required
                            inputRef={field.ref}
                            inputProps={{
                              id: `organizations-customType-${index}`,
                            }}
                            InputLabelProps={{
                              htmlFor: `organizations-customType-${index}`,
                            }}
                            disabled={disabled}
                            name="organizations"
                            label="Custom Type"
                            error={!!orgErrors?.[index]?.customType}
                            helperText={
                              orgErrors?.[index]?.customType
                                ? 'This field is required.'
                                : ''
                            }
                            sx={{
                              minWidth: 160,
                            }}
                          />
                        )}
                      />
                    )}
                    <Typography
                      variant="caption"
                      sx={{
                        whiteSpace: 'pre-line',
                      }}
                    >
                      {`${
                        watch(`${fieldName}.${index}.name`) ||
                        'No name provided'
                      }`}
                    </Typography>
                    <Tooltip title={'Edit Organization'} arrow>
                      <span>
                        <IconButton
                          size="small"
                          disabled={disabled}
                          onClick={() => {
                            setClickedIndex(index);
                            setOpenOrganizationDialog(true);
                          }}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>

                  {/* actions */}
                  <Stack flexDirection="row" gap={2}>
                    <Controller
                      name={`${fieldName}.${index}.primary`}
                      control={control}
                      render={({ field }) => {
                        return (
                          <FormControlLabel
                            control={
                              <Tooltip title="This determines what is shown in Google Admin panel.">
                                <Checkbox
                                  onChange={() => {
                                    field.onChange(!field.value);

                                    // set all other primary values to false
                                    fields.forEach((_item, i) => {
                                      if (i !== index) {
                                        setValue(
                                          `${fieldName}.${i}.primary`,
                                          false
                                        );
                                      }
                                    });
                                  }}
                                  size="small"
                                  checked={field.value === true}
                                />
                              </Tooltip>
                            }
                            label="primary"
                          />
                        );
                      }}
                    />

                    <Tooltip title="Move Down" arrow>
                      <span>
                        <IconButton
                          disabled={index === fields.length - 1 || disabled}
                          size="small"
                          onClick={() => {
                            move(index, index + 1);
                          }}
                        >
                          <ArrowDownward />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Move Up" arrow>
                      <span>
                        <IconButton
                          disabled={index === 0 || disabled}
                          size="small"
                          onClick={() => {
                            move(index, index - 1);
                          }}
                        >
                          <ArrowUpward />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Delete" arrow>
                      <span>
                        <IconButton
                          size="small"
                          disabled={disabled}
                          onClick={() => {
                            remove(index);
                          }}
                        >
                          <Delete />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                </Stack>
                {index !== fields.length - 1 && <Divider />}
              </Box>
            );
          })}
        </AccordionDetails>
      </Accordion>
      {openOrganizationDialog ? (
        <PolicyOrganizationEditDialog
          openOrganizationDialog={openOrganizationDialog}
          setOpenOrganizationDialog={setOpenOrganizationDialog}
          clickedIndex={clickedIndex}
          setValue={setValue}
          disabled={!!disabled}
          getValues={getValues}
          fieldName={fieldName}
        />
      ) : null}
    </>
  );
}
